import { useState, useEffect } from 'react'
import { UserPlus, Trash2, X, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  listTenantUsers,
  setTenantUserRole,
  removeTenantUser,
  TenantUser,
} from '../../../services/api'

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  editor: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
}

interface Props {
  tenantId: string
}

export default function AdminUserManager({ tenantId }: Props) {
  const [users, setUsers] = useState<TenantUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState('editor')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [tenantId])

  async function loadUsers() {
    try {
      setLoading(true)
      const data = await listTenantUsers(tenantId)
      setUsers(data.users || [])
    } catch (err) {
      console.error('Failed to load users:', err)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  async function handleAddUser() {
    if (!newEmail.trim()) return
    setAdding(true)
    try {
      await setTenantUserRole(tenantId, newEmail.trim(), newRole)
      toast.success(`Added ${newEmail.trim()} as ${newRole}`)
      setNewEmail('')
      setShowAddForm(false)
      await loadUsers()
    } catch (err: any) {
      toast.error(err.message || 'Failed to add user')
    } finally {
      setAdding(false)
    }
  }

  async function handleChangeRole(email: string, role: string) {
    try {
      await setTenantUserRole(tenantId, email, role)
      toast.success(`Updated role to ${role}`)
      await loadUsers()
    } catch (err: any) {
      toast.error(err.message || 'Failed to change role')
    }
  }

  async function handleRemoveUser(email: string) {
    if (!confirm(`Remove ${email} from this company?`)) return
    try {
      await removeTenantUser(tenantId, email)
      toast.success(`Removed ${email}`)
      await loadUsers()
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove user')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">
          Team Members ({users.length})
        </h4>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <UserPlus className="w-3.5 h-3.5" />
          Add User
        </button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddUser()}
              placeholder="user@company.com"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg outline-none bg-white"
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <button
            onClick={handleAddUser}
            disabled={!newEmail.trim() || adding}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
          </button>
          <button
            onClick={() => { setShowAddForm(false); setNewEmail('') }}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Users Table */}
      {users.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No users yet</p>
      ) : (
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2">Email</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2">Role</th>
                <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((u) => (
                <tr key={u.email} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 text-sm text-gray-900">{u.email}</td>
                  <td className="px-4 py-2.5">
                    {u.role === 'owner' ? (
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${ROLE_COLORS.owner}`}>
                        OWNER
                      </span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) => handleChangeRole(u.email, e.target.value)}
                        className={`px-2 py-0.5 text-xs font-medium rounded-full border-0 outline-none cursor-pointer ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {u.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveUser(u.email)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove user"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
